import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import { PATH } from 'constants/path';
import IntroStep from './components/IntroStep';
import BillingPoliciesStep from './components/BillingPoliciesStep';
import ConsentFormsStep from './components/ConsentFormsStep';
import PracticeProfileStep from './components/PracticeProfileStep';
import BusinessInfoStep from './components/BusinessInfoStep';
import PayoutAccountStep from './components/PayoutAccountStep';
import ReviewStep from './components/ReviewStep';
import StepIndicator from './components/StepIndicator';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { EKey, getUserId } from 'utils/localstore';
import { getAccountConnectSecret } from 'api/paymentSetup';
import StripeModal from './components/StripeModal';

const LOCAL_STORAGE_KEY_PREFIX = EKey.PAYMENT_SETUP_STEP;
const getLocalStorageKey = () => {
    const userId = getUserId();
    return `${LOCAL_STORAGE_KEY_PREFIX}_${userId}`;
};

const PaymentSetup: React.FC = () => {
    const navigate = useNavigate();
    const [accountConnectSecret, setAccountConnectSecret] = useState<string>('');
    const [showStripeModal, setShowStripeModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const ref = useRef<any>();
    const steps = [
        { id: 0, title: '', component: <IntroStep /> },
        { id: 1, title: 'Appointment billing policies', component: <BillingPoliciesStep ref={ref} /> },
        { id: 2, title: 'Practice profile', component: <PracticeProfileStep ref={ref} /> },
        { id: 3, title: 'Consent forms', component: <ConsentFormsStep ref={ref} /> },
        { id: 4, title: 'Business information', component: <BusinessInfoStep ref={ref} /> },
        { id: 5, title: 'Payout account', component: <PayoutAccountStep ref={ref} /> },
        { id: 6, title: 'Review', component: <ReviewStep ref={ref} /> },
    ];
    const [currentStep, setCurrentStep] = useState<number>(() => {
        const savedStep = localStorage.getItem(getLocalStorageKey());
        if (savedStep) {
            return parseInt(savedStep, 10);
        }
        return 0;
    });
    const stepTitles = steps.slice(1, -1).map((step) => ({ id: step.id, title: step.title }));

    const [loadBasicData] = useLoadBasicData();

    useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    useEffect(() => {
        localStorage.setItem(getLocalStorageKey(), currentStep.toString());
    }, [currentStep]);

    const scrollToTop = useCallback(() => {
        document.querySelector(`.${s.content}`)?.scrollTo(0, 0);
    }, []);

    const goToNextStep = useCallback(async () => {
        try {
            setLoading(true);
            await ref.current?.onSubmit();
            if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
                scrollToTop();
            } else {
                const res = await getAccountConnectSecret();
                if (res.data.data) {
                    setAccountConnectSecret(res.data.data);
                    setShowStripeModal(true);
                } else {
                    message.error(res.data.message);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentStep, scrollToTop]);

    const goToPreviousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            scrollToTop();
        }
    }, [currentStep, scrollToTop]);

    const handleStepClick = useCallback((step: number) => {
        if (step > 0 && step < steps.length - 1) {
            setCurrentStep(step);
            scrollToTop();
        }
    }, [scrollToTop]);

    const renderStepContent = steps[currentStep].component;

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <img
                    onClick={() => {
                        navigate(PATH.DASHBOARD);
                    }}
                    src="https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/ui/logo_kiwihealth_by_klarity_380x96.png"
                    className={s.logo}
                    alt="Kiwi Health Logo"
                />
            </div>

            <div className={s.content}>
                {currentStep > 0 && currentStep < steps.length - 1 && (
                    <div className={s.stepIndicatorContainer}>
                        <StepIndicator
                            steps={stepTitles}
                            currentStep={currentStep}
                            onStepClick={handleStepClick}
                        />
                    </div>
                )}

                <div className={s.stepContent}>
                    {renderStepContent}
                </div>
            </div>

            <div className={s.footer}>
                <div className={s.btns}>
                    <Button
                        onClick={() => {
                            navigate(`${PATH.DASHBOARD}`);
                        }}
                    >
                        Exit
                    </Button>
                    {currentStep > 0 && (
                        <Button
                            type="text"
                            onClick={goToPreviousStep}
                            className={s.prevButton}
                        >
                            Back
                        </Button>
                    )}
                </div>
                <Button type="primary" className={s.exitButton} onClick={goToNextStep} loading={loading}>
                    {currentStep === steps.length - 1 ? 'Submit' : 'Next'}
                </Button>
            </div>
            <StripeModal
                showStripeModal={showStripeModal}
                onClose={(isExit) => {
                    setShowStripeModal(false);
                    if (isExit) {
                        navigate(`/${PATH.PAYMENT_SETUP_SUCCESS}`);
                    }
                }}
                clientSecret={accountConnectSecret}
            />
        </div>
    );
};

export default PaymentSetup;
